import React, {useState} from 'react';
import PropTypes from 'prop-types';
import GlobalControl from '../internal_components/_GlobalControl.react';
import {getTitleDescriptionId} from '../util/componentProps';

function TabContents({selectedControlGroup}) {
    if (!selectedControlGroup) {
        return null;
    }

    const description = selectedControlGroup.description.trim();
    return (
        <>
            {description && (
                <div className="tab-description">{description}</div>
            )}
            <div className="tab-controls">
                {selectedControlGroup.children.map(child => {
                    const {title, description, id} =
                        getTitleDescriptionId(child);

                    return (
                        <GlobalControl
                            key={id}
                            label={title}
                            tooltip={description}
                        >
                            {child}
                        </GlobalControl>
                    );
                })}
            </div>
        </>
    );
}

TabContents.propTypes = {
    selectedControlGroup: PropTypes.shape({
        /**
         * The title of the tab.
         */
        title: PropTypes.string.isRequired,
        /**
         * The description of the tab.
         */
        description: PropTypes.string.isRequired,
        /**
         * The id of the tab.
         */
        id: PropTypes.string.isRequired,
        /**
         * The control elements to be placed in the corresponding section.
         */
        children: PropTypes.arrayOf(PropTypes.node).isRequired,
    }),
};

function TabNavigation({
    control_groups,
    selectedTabId,
    setSelectedTabId,
    action_button,
}) {
    return (
        <div className="tab-navigation-container">
            <div className="tab-navigation">
                {control_groups.map(group => {
                    const classes = [
                        'tab-title',
                        selectedTabId === group.id ? 'selected' : '',
                    ];
                    return (
                        <div
                            role="button"
                            tabIndex="0"
                            className={classes.join(' ')}
                            title={group.title}
                            key={group.id}
                            onClick={() => setSelectedTabId(group.id)}
                        >
                            {group.title}
                        </div>
                    );
                })}
            </div>
            {action_button}
        </div>
    );
}
TabNavigation.propTypes = {
    control_groups: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * The title of the tab.
             */
            title: PropTypes.string.isRequired,
            /**
             * The description of the tab.
             */
            description: PropTypes.string.isRequired,
            /**
             * The id of the tab.
             */
            id: PropTypes.string.isRequired,
            /**
             * The control elements to be placed in the corresponding section.
             */
            children: PropTypes.arrayOf(PropTypes.node).isRequired,
        })
    ).isRequired,
    selectedTabId: PropTypes.string.isRequired,
    setSelectedTabId: PropTypes.func.isRequired,
    /**
     * The action button to be placed in the controls.
     */
    action_button: PropTypes.node,
};

/**
 * A container for organizing control elements in a collapsible sticky header
 * with tabs.
 *
 * WARNING: This is an alpha version of a component. Breaking changes may be
 * made without respecting semantic versioning. Use at your own risk.
 *
 * **Example Usage**
 * ```
 * ddk._TabbedControls(
 *     control_groups=[
 *         {
 *             "title": "Model Parameters",
 *             "children": [
 *                 dcc.Slider(
 *                     name="n", min=0, max=10, marks={0: "0", 5: "5", 10: "10"}, value=5
 *                 ),
 *             ],
 *         },
 *         {
 *             "title": "Plot Axes",
 *             "children": [
 *                 dcc.Dropdown(
 *                     name="x-axis",
 *                     placeholder="City",
 *                     options=[
 *                         {"label": "New York City", "value": "NYC"},
 *                         {"label": "Montreal", "value": "MTL"},
 *                     ],
 *                     value="MTL",
 *                 ),
 *             ],
 *         },
 *     ]
 * )
 * ```
 */
export default function _TabbedControls({control_groups, action_button}) {
    const [selectedTabId, setSelectedTabId] = useState(control_groups[0].id);
    const selectedControlGroup = control_groups.find(
        tab => tab.id === selectedTabId
    );
    return (
        <div className="tabbed-controls">
            <TabNavigation
                control_groups={control_groups}
                selectedTabId={selectedTabId}
                setSelectedTabId={setSelectedTabId}
                action_button={action_button}
            ></TabNavigation>
            <TabContents
                selectedControlGroup={selectedControlGroup}
            ></TabContents>
        </div>
    );
}

_TabbedControls.propTypes = {
    /**
     * The control elements to be positioned in the Wrapper horizontally or
     * vertically, grouped by section.
     */
    control_groups: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * The title of the tab.
             */
            title: PropTypes.string.isRequired,
            /**
             * The description of the tab.
             */
            description: PropTypes.string.isRequired,
            /**
             * The id of the tab.
             */
            id: PropTypes.string.isRequired,
            /**
             * The control elements to be placed in the corresponding section.
             */
            children: PropTypes.arrayOf(PropTypes.node).isRequired,
        })
    ).isRequired,
    /**
     * The action button to be placed in the control panel.
     */
    action_button: PropTypes.node,
};
